import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useAcceptInvitation, useDeleteUserRole, useUserRole } from '@/models/userRole';
import { useCurrentSession } from '@/services/auth';
import { ORGANIZATION_LOCAL_STORAGE_KEY } from '@/utils/organization';

const Organization = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { organization } = useParams();
  const { currentSession } = useCurrentSession();

  const [error, setError] = useState('');

  const { data: userRole, isLoading } = useUserRole({
    slug: organization!,
  });

  const {
    mutateAsync: deleteUserRole,
    isPending: isPendingDeleteUserRole,
    isSuccess: isSuccessDeleteUserRole,
  } = useDeleteUserRole();

  const {
    mutateAsync: acceptInvitation,
    isPending: isPendingAcceptInvitation,
    isSuccess: isSuccessAcceptInvitation,
  } = useAcceptInvitation();

  if (isLoading) {
    return null;
  }

  if (!userRole) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Title size="200" as="h1" align="center">
        <Trans id="join.organization.title">
          You have been invited to join the {userRole.organization.name} organization
        </Trans>
      </Title>
      <Paragraph align="center">
        <Trans id="join.organization.subtitle">
          By accepting the invitation, you will be able to access the organization's data and
          contribute to it.
        </Trans>
      </Paragraph>
      {error && (
        <InlineText weight="medium" color="danger" align="center">
          {error}
        </InlineText>
      )}
      <Stack row justifyContent="center" gap="1rem">
        <Button
          variant="secondary"
          type="button"
          isLoading={isPendingDeleteUserRole}
          disabled={
            isSuccessDeleteUserRole || isPendingAcceptInvitation || isSuccessAcceptInvitation
          }
          onPress={() => {
            deleteUserRole({ email: currentSession!.email })
              .then(() => navigate('/', { replace: true }))
              .catch((err) => {
                console.error(err);
                setError(
                  (err.message as string) ??
                    t({ id: '_general.error.unknown', message: 'Unknown error' })
                );
              });
          }}
        >
          <Trans id="join.organization.decline">Decline invitation</Trans>
        </Button>
        <Button
          variant="primary"
          type="button"
          isLoading={isPendingAcceptInvitation}
          disabled={isSuccessAcceptInvitation || isPendingDeleteUserRole || isSuccessDeleteUserRole}
          onPress={() => {
            acceptInvitation({ slug: userRole.organization.slug })
              .then(() => {
                // setCurrentOrganization of useCurrentOrganization seems to redirect too fast, so we do it manually
                localStorage.setItem(
                  ORGANIZATION_LOCAL_STORAGE_KEY,
                  `"${userRole.organization.slug}"`
                );
                window.location.href = '/';
              })
              .catch((err) => {
                console.error(err);
                setError(
                  (err.message as string) ??
                    t({ id: '_general.error.unknown', message: 'Unknown error' })
                );
              });
          }}
        >
          <Trans id="join.organization.accept">Accept invitation</Trans>
        </Button>
      </Stack>
    </>
  );
};

export default Organization;
